import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Printer, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/certificate/$enrollmentId")({
  component: CertificatePrintPage,
});

function CertificatePrintPage() {
  const { enrollmentId } = Route.useParams();

  const { data, isLoading: loading } = useQuery({
    queryKey: ["certificate", enrollmentId],
    queryFn: async () => {
      const { data: enrollment, error } = await supabase
        .from("enrollments")
        .select(
          `
          status, 
          updated_at,
          profiles(full_name, points),
          courses(name)
        `,
        )
        .eq("id", enrollmentId)
        .single();

      if (error) throw error;

      if (enrollment && enrollment.status === "completed") {
        return {
          date: enrollment.updated_at,
          courseName: (enrollment.courses as any)?.name || "دورة برمجية",
          studentName: (enrollment.profiles as any)?.full_name || "طالب متميز",
          points: (enrollment.profiles as any)?.points || 0,
        };
      }
      return null;
    },
  });

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col h-screen items-center justify-center bg-background text-center p-4">
        <h1 className="text-2xl font-bold mb-2">الشهادة غير متاحة</h1>
        <p className="text-muted-foreground mb-6">
          لم يتم العثور على الشهادة أو أن الدورة لم تكتمل بعد.
        </p>
        <Button asChild rounded-full>
          <Link to="/">العودة للرئيسية</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col items-center p-4 sm:p-8">
      <div className="w-full max-w-4xl flex justify-between items-center mb-6 print:hidden">
        <Button variant="outline" asChild className="rounded-full">
          <Link to="/student/certificates">
            <ChevronRight className="h-4 w-4 me-2" /> رجوع
          </Link>
        </Button>
        <Button
          onClick={() => window.print()}
          className="rounded-full bg-gradient-primary text-primary-foreground border-0 shadow-soft"
        >
          <Printer className="h-4 w-4 me-2" /> طباعة الشهادة
        </Button>
      </div>

      {/* Certificate Container */}
      <div
        className="relative w-full max-w-[1000px] aspect-[1.414/1] bg-white text-slate-900 shadow-2xl overflow-hidden print:shadow-none print:w-[100%] print:h-[100vh] print:max-w-none flex flex-col"
        style={{ direction: "rtl", pageBreakAfter: "always" }}
      >
        {/* Background Design */}
        <div className="absolute inset-0 border-[20px] border-primary/10"></div>
        <div className="absolute inset-4 border-2 border-primary/20"></div>

        {/* Decorative Corners */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/10 rounded-tr-full"></div>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-12 text-center">
          <div className="mb-8">
            <h1
              className="text-5xl font-black text-primary tracking-tight mb-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              CODE CUBS
            </h1>
            <p className="text-xl font-bold text-slate-500 tracking-widest uppercase">Academy</p>
          </div>

          <div className="space-y-6 max-w-2xl">
            <h2 className="text-4xl font-extrabold text-slate-800">شهادة إتمام دورة</h2>
            <p className="text-lg text-slate-600">تشهد أكاديمية Code Cubs بأن الطالب المتميز</p>

            <h3 className="text-5xl font-bold text-primary py-4">{data.studentName}</h3>

            <p className="text-lg text-slate-600">قد أتم بنجاح كافة متطلبات اجتياز دورة</p>
            <h4 className="text-3xl font-bold text-slate-800">{data.courseName}</h4>

            <p className="text-slate-500 pt-4">متمنين له دوام التوفيق والنجاح في مسيرته البرمجية</p>
          </div>

          <div className="w-full flex justify-between items-end mt-16 px-12">
            <div className="text-center">
              <div className="w-40 border-b-2 border-slate-300 mb-2"></div>
              <p className="text-sm font-bold text-slate-600">تاريخ الإصدار</p>
              <p className="text-slate-500">{new Date(data.date).toLocaleDateString("ar-EG")}</p>
            </div>

            <div className="h-32 w-32 relative flex items-center justify-center">
              <div className="absolute inset-0 border-4 border-amber-400 rounded-full rotate-45 border-dashed"></div>
              <div className="h-28 w-28 bg-amber-400 text-white rounded-full flex flex-col items-center justify-center shadow-lg">
                <span className="text-xs font-bold uppercase">Points</span>
                <span className="text-2xl font-black">{data.points}</span>
              </div>
            </div>

            <div className="text-center">
              <div className="w-40 border-b-2 border-slate-300 mb-2"></div>
              <p className="text-sm font-bold text-slate-600">توقيع الأكاديمية</p>
              <p className="text-slate-500" style={{ fontFamily: "Outfit, sans-serif" }}>
                Code Cubs
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Global Print Styles to force landscape and remove margins */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .dashboard-safe-area, header, footer {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
